import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import { api } from "@/lib/api";
import { RESTAURANT } from "@/lib/restaurant";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir les champs obligatoires."); return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé !");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" description="Contactez le Globo Dancing à Trappes : Avenue Johanne Kepler 78190. Tel : 01 30 50 46 14. Email : globodancing78@gmail.com. Horaires et itinéraire Google Maps." path="/contact"/>
      <section className="pt-32 pb-10 azulejo-bg">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="eyebrow">Falar connosco</div>
          <h1 className="text-5xl md:text-7xl mt-4 leading-[0.95]">Nous <em className="italic text-[#C04E38]">contacter</em></h1>
          <p className="text-[#5C5A56] text-lg mt-5 max-w-2xl">
            Une question, un grand groupe, une privatisation ? Appelez-nous ou écrivez-nous,
            on vous répond avec plaisir.
          </p>

          {/* Big phone CTA */}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <a href={`tel:${RESTAURANT.phone.replace(/\s/g,'')}`} className="card-warm p-5 flex items-center gap-4 hover:!bg-[#C04E38] hover:!text-white transition group" data-testid="contact-cta-phone">
              <div className="w-12 h-12 rounded-full bg-[#C04E38]/10 group-hover:bg-white/20 grid place-items-center text-[#C04E38] group-hover:text-white"><Phone size={20}/></div>
              <div><div className="text-xs uppercase tracking-widest text-[#5C5A56] group-hover:text-white/80">Téléphone</div><div className="font-serif-display text-xl">{RESTAURANT.phone}</div></div>
            </a>
            <a href={RESTAURANT.mapDirectionsUrl} target="_blank" rel="noopener noreferrer" className="card-warm p-5 flex items-center gap-4 hover:!bg-[#2C2B29] hover:!text-white transition group" data-testid="contact-cta-directions">
              <div className="w-12 h-12 rounded-full bg-[#2C2B29]/10 group-hover:bg-white/20 grid place-items-center text-[#2C2B29] group-hover:text-white"><Navigation size={20}/></div>
              <div><div className="text-xs uppercase tracking-widest text-[#5C5A56] group-hover:text-white/80">Itinéraire</div><div className="font-serif-display text-xl">M'y conduire</div></div>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-10">
          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-3 card-warm p-8 md:p-10 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className="text-sm font-medium mb-1.5 block">Nom *</label>
                <input className="input-warm" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="contact-name"/></div>
              <div><label className="text-sm font-medium mb-1.5 block">Email *</label>
                <input type="email" className="input-warm" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="contact-email"/></div>
            </div>
            <div><label className="text-sm font-medium mb-1.5 block">Sujet</label>
              <input className="input-warm" value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})} data-testid="contact-subject"/></div>
            <div><label className="text-sm font-medium mb-1.5 block">Message *</label>
              <textarea rows={6} className="input-warm" required value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} data-testid="contact-message"/></div>
            <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit">
              {sending ? <><Loader2 className="animate-spin mr-2" size={18}/> Envoi…</> : "Envoyer le message"}
            </button>
          </form>

          <aside className="lg:col-span-2 space-y-5">
            <div className="card-warm p-6">
              <h3 className="text-xl mb-4">Coordonnées</h3>
              <ul className="space-y-3 text-sm text-[#5C5A56]">
                <li className="flex gap-3"><MapPin size={16} className="mt-0.5 text-[#C04E38]" /> {RESTAURANT.fullAddress}</li>
                <li className="flex gap-3"><Phone size={16} className="mt-0.5 text-[#C04E38]" /> <a href={`tel:${RESTAURANT.phone.replace(/\s/g,'')}`} className="hover:text-[#C04E38]">{RESTAURANT.phone}</a></li>
                <li className="flex gap-3"><Mail size={16} className="mt-0.5 text-[#C04E38]" /> <a href={`mailto:${RESTAURANT.email}`} className="hover:text-[#C04E38] break-all">{RESTAURANT.email}</a></li>
              </ul>
            </div>
            <div className="card-warm p-6">
              <h3 className="text-xl mb-4 flex items-center gap-2"><Clock size={18} className="text-[#C04E38]"/> Horaires</h3>
              <ul className="space-y-2 text-sm text-[#5C5A56]">
                {RESTAURANT.hours.map((h,i)=>(<li key={i}>{h.d} : {h.h}</li>))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#E6E1D6]">
              <iframe title="Carte Trappes" src={RESTAURANT.mapEmbedUrl} className="w-full h-64" loading="lazy" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
